import React from "react";
import { useAdmin } from "../AdminLayout/AdminProvider";
import {
  MapPin,
  FolderOpen,
  UserRound,
  Users,
  Building,
  TrendingUp,
  BarChart3,
} from "lucide-react";


export const Dashboard = () => {
  const {
    zones,
    projects,
    projectHeads,
    associates,
    propertyOwners,
  } = useAdmin();

  const stats = [
    {
      title: "Total Zones",
      value: zones.length,
      icon: <MapPin size={24} />,
      bg: "#7f4070",
    },
    {
      title: "Total Projects",
      value: projects.length,
      icon: <FolderOpen size={24} />,
      bg: "#4a8c7c",
    },
    {
      title: "Project Heads",
      value: projectHeads.length,
      icon: <UserRound size={24} />,
      bg: "#7f4070",
    },
    {
      title: "Associates",
      value: associates.length,
      icon: <Users size={24} />,
      bg: "#4a8c7c",
    },
    {
      title: "Property Owners",
      value: propertyOwners.length,
      icon: <Building size={24} />,
      bg: "#7f4070",
    },
    {
      title: "Total Plots",
      value: projects.reduce((sum, p) => sum + p.plots, 0),
      icon: <BarChart3 size={24} />,
      bg: "#4a8c7c",
    },
  ];

  const totalEntities =
    zones.length +
    projects.length +
    projectHeads.length +
    associates.length +
    propertyOwners.length;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh", // fill viewport
        overflow: "hidden", // prevent body scroll
      }}
    >
 

      {/* Main Content */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // prevent main content overflow
        }}
      >
        {/* Scrollable Dashboard Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 24,
            backgroundColor: "#f9f9f9",
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 30, fontWeight: "bold", marginBottom: 8 }}>
              Dashboard
            </h1>
            <p style={{ color: "#666" }}>
              Welcome to Real Estate Admin Management System
            </p>
          </div>

          {/* Stats Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 24,
              marginBottom: 32,
            }}
          >
            {stats.map((stat, index) => (
              <div
                key={index}
                style={{
                  border: "1px solid #000",
                  borderRadius: 8,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                }}
              >
                <div
                  style={{
                    backgroundColor: stat.bg,
                    padding: 16,
                    color: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <p style={{ fontSize: 14, opacity: 0.8 }}>{stat.title}</p>
                    <p style={{ fontSize: 28, fontWeight: "bold" }}>
                      {stat.value}
                    </p>
                  </div>
                  {stat.icon}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ display: "flex", gap: 6, color: "#666" }}>
                    <TrendingUp size={16} color="#4a8c7c" />
                    <span style={{ fontSize: 14 }}>Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Projects and Zones */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
              gap: 24,
              marginBottom: 32,
            }}
          >
            {/* Recent Projects */}
            <div style={{ border: "1px solid #000", padding: 24, borderRadius: 8, backgroundColor: "#fff" }}>
              <h2 style={{ fontSize: 20, marginBottom: 16 }}>Recent Projects</h2>
              {projects.length === 0 ? (
                <p style={{ textAlign: "center", color: "#888" }}>No projects added yet</p>
              ) : (
                projects.slice(0, 5).map((project) => (
                  <div
                    key={project.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: 12,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 6,
                      marginBottom: 8,
                    }}
                  >
                    <div>
                      <p style={{ fontWeight: "bold" }}>{project.name}</p>
                      <p style={{ fontSize: 13, color: "#666" }}>{project.area}</p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ color: "#D4AF37", fontWeight: "bold" }}>
                        {project.plots} Plots
                      </p>
                      <p style={{ fontSize: 12, color: "#666" }}>{project.size}</p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Zone Distribution */}
            <div style={{ border: "1px solid #000", padding: 24, borderRadius: 8, backgroundColor: "#fff" }}>
              <h2 style={{ fontSize: 20, marginBottom: 16 }}>Zone Distribution</h2>
              {zones.map((zone) => {
                const zoneProjects = projects.filter((p) => p.zoneId === zone.id);
                return (
                  <div
                    key={zone.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: 12,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 6,
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ display: "flex", gap: 12 }}>
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "#7f407020",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <MapPin size={20} color="#7f4070" />
                      </div>
                      <div>
                        <p style={{ fontWeight: "bold" }}>{zone.id}</p>
                        <p style={{ fontSize: 13, color: "#666" }}>
                          {zone.description}
                        </p>
                      </div>
                    </div>
                    <p style={{ color: "#4a8c7c", fontWeight: "bold" }}>
                      {zoneProjects.length} Projects
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* System Overview */}
          {/* <div
            style={{
              background: "linear-gradient(to right, #7f4070, #4a8c7c)",
              color: "#fff",
              padding: 24,
              borderRadius: 8,
            }}
          >
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>System Overview</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 24,
              }}
            >
              <div>
                <p style={{ opacity: 0.7 }}>Total Entities</p>
                <p style={{ fontSize: 28, fontWeight: "bold" }}>{totalEntities}</p>
              </div>
              <div>
                <p style={{ opacity: 0.7 }}>Active Zones</p>
                <p style={{ fontSize: 28, fontWeight: "bold" }}>{zones.length}</p>
              </div>
              <div>
                <p style={{ opacity: 0.7 }}>System Status</p>
                <p style={{ fontSize: 20, color: "#D4AF37" }}>Operational</p>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};
